import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Building2, LogIn, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

const CONSENT_VERSION = "2026-01";

export const InvitationPage = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation("workspace");
  const navigate = useNavigate();
  const { user, login, loadWorkspaces, switchWorkspace } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [working, setWorking] = useState(false);
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalError, setLegalError] = useState(false);

  const loadInvite = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invitations/${token}`);
      setInvitation(data);
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInvite();
  }, [loadInvite]);

  const finishJoin = async () => {
    const { data } = await api.post(`/invitations/${token}/accept`);
    await loadWorkspaces();
    switchWorkspace(data.workspace_id);
    toast.success(t("invite.joined", { name: data.workspace_name }));
    navigate("/compte");
  };

  const acceptExisting = async () => {
    setWorking(true);
    try {
      await finishJoin();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setWorking(false);
    }
  };

  const registerAndJoin = async (e) => {
    e.preventDefault();
    if (!legalAccepted) {
      setLegalError(true);
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.post("/account/register", {
        company: invitation.workspace_name || "",
        first_name: firstName,
        last_name: lastName,
        email: invitation.email,
        password,
        consents: {
          terms: { version: CONSENT_VERSION },
          privacy: { version: CONSENT_VERSION },
        },
      });
      await login(data.token, data.user);
      await finishJoin();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="app-bg grain min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const invalid = notFound || !invitation || invitation.status !== "pending";
  const isFrench = (i18n.language || "fr").toLowerCase().startsWith("fr");

  return (
    <div className="app-bg grain min-h-screen flex items-center justify-center px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="glass rounded-2xl p-8 w-full max-w-md"
        data-testid="invitation-page"
      >
        <Link to="/" className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="font-heading font-extrabold tracking-tight text-lg">
            Secure<span className="text-emerald-600">Pass</span>
          </div>
        </Link>

        {invalid ? (
          <div data-testid="invitation-invalid">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-2">
              {t("invite.invalidTitle")}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {t("invite.invalid")}
            </p>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="w-full border-slate-200 bg-slate-100 hover:bg-slate-100 text-slate-900"
            >
              {t("invite.home")}
            </Button>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
              <Building2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-1">
              {t("invite.joinTitle", { name: invitation.workspace_name })}
            </h2>
            <div className="text-sm text-slate-500 mb-4 flex items-center gap-2 flex-wrap">
              <span>{t("invite.invitedAs")}</span>
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">
                {t(`common:roles.${invitation.role}`, { defaultValue: invitation.role_label })}
              </Badge>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 mb-6 text-sm">
              <span className="text-slate-500">{t("invite.invitedEmail")}</span>{" "}
              <span className="font-medium text-slate-900" data-testid="invitation-email">{invitation.email}</span>
            </div>

            {user ? (
              user.email?.toLowerCase() === invitation.email.toLowerCase() ? (
                <Button
                  data-testid="join-org-btn"
                  onClick={acceptExisting}
                  disabled={working}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                >
                  {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  {t("invite.join")}
                </Button>
              ) : (
                <div className="text-sm text-amber-600" data-testid="invitation-wrong-account">
                  {t("invite.wrongAccount", { current: user.email, invited: invitation.email })}
                </div>
              )
            ) : invitation.account_exists ? (
              <div className="space-y-3" data-testid="invitation-login-cta">
                <p className="text-sm text-slate-500">
                  {t("invite.accountExists")}
                </p>
                <Button
                  data-testid="invitation-goto-login"
                  onClick={() => navigate(`/connexion?next=/invitation/${token}`)}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                >
                  <LogIn className="w-4 h-4 mr-2" /> {t("invite.login")}
                </Button>
              </div>
            ) : (
              <form onSubmit={registerAndJoin} data-testid="invitation-register-form" className="space-y-4">
                <p className="text-sm text-slate-500">
                  {t("invite.createIntro")}
                </p>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{t("invite.email")}</Label>
                  <Input
                    data-testid="invitation-email-locked"
                    value={invitation.email}
                    readOnly
                    disabled
                    className="bg-slate-100 border-slate-200 text-slate-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-600 mb-1.5 block">{t("invite.firstName")}</Label>
                    <Input
                      data-testid="invitation-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="bg-slate-50 border-slate-200"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 mb-1.5 block">{t("invite.lastName")}</Label>
                    <Input
                      data-testid="invitation-last-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="bg-slate-50 border-slate-200"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{t("invite.password")}</Label>
                  <Input
                    data-testid="invitation-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("invite.passwordPh")}
                    className="bg-slate-50 border-slate-200"
                    required
                  />
                </div>

                <div>
                  <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="invitation-legal-consent"
                      checked={legalAccepted}
                      onChange={(e) => {
                        setLegalAccepted(e.target.checked);
                        if (e.target.checked) setLegalError(false);
                      }}
                      className="mt-1 h-4 w-4 accent-emerald-500"
                    />
                    <span>
                      {isFrench ? "J’accepte les " : "I accept the "}
                      <Link to="/cgu" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">
                        {isFrench ? "Conditions Générales d’Utilisation" : "Terms of Use"}
                      </Link>
                      {isFrench ? " et reconnais avoir pris connaissance de la " : " and acknowledge that I have read the "}
                      <Link to="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">
                        {isFrench ? "Politique de confidentialité" : "Privacy Policy"}
                      </Link>
                      .
                    </span>
                  </label>
                  {legalError && (
                    <p className="mt-2 text-sm text-red-600" data-testid="invitation-legal-error">
                      {isFrench
                        ? "Vous devez accepter les Conditions Générales d’Utilisation pour créer votre compte."
                        : "You must accept the Terms of Use to create your account."}
                    </p>
                  )}
                </div>

                <Button
                  data-testid="invitation-register-submit"
                  type="submit"
                  disabled={working}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                >
                  {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  {t("invite.submit")}
                </Button>
              </form>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};
